import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { type ActiveOperation, operationService } from "@/lib/services";
import { useAuth } from "./AuthContext";
import { useNotificationEvent } from "./NotificationContext";

// ── Types ────────────────────────────────────────────────────────────────────

/** An operation that has reached a terminal state (completed / failed). */
export interface CompletedOperation extends ActiveOperation {
  /** ISO timestamp when the operation finished. */
  completed_at: string;
}

interface OperationContextType {
  /** All currently active operations for this user. */
  activeOperations: ActiveOperation[];
  /** Recently completed/failed operations (most-recent first, max 10, pruned after 30 min). */
  recentlyCompleted: CompletedOperation[];
  /** Number of active operations. */
  operationCount: number;
  /** Look up a specific operation by ID. */
  getOperation: (opId: string) => ActiveOperation | undefined;
  /** Find an active operation by entity_id (e.g. task UUID, session UUID). */
  findByEntityId: (entityId: string) => ActiveOperation | undefined;
  /** Manually refresh the active operations list. */
  refresh: () => Promise<void>;
  /** Update a tracked operation's status (called by useOperationStream or notification handler). */
  updateOperationStatus: (opId: string, status: string) => void;
  /** Add a new operation to the tracked list. */
  trackOperation: (op: ActiveOperation) => void;
  /** Remove an operation from the tracked list (terminal state). */
  removeOperation: (opId: string) => void;
}

const OperationContext = createContext<OperationContextType | null>(null);

/** Polling interval for background refresh (ms). */
const REFRESH_INTERVAL = 30_000;

/** Max recently-completed entries to keep. */
const MAX_RECENT = 10;

/** Auto-prune recently-completed entries older than this (ms). */
const RECENT_TTL_MS = 30 * 60 * 1000; // 30 minutes

// ── Provider ─────────────────────────────────────────────────────────────────

export function OperationProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { isAuthenticated } = useAuth();
  const [operations, setOperations] = useState<ActiveOperation[]>([]);
  const [recentlyCompleted, setRecentlyCompleted] = useState<CompletedOperation[]>([]);
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  // ── Helper: move an operation to the recently-completed list ───
  const moveToRecent = useCallback((op: ActiveOperation, finalStatus: string) => {
    setRecentlyCompleted((prev) => {
      // Avoid duplicates
      if (prev.some((r) => r.operation_id === op.operation_id)) return prev;
      const entry: CompletedOperation = {
        ...op,
        status: finalStatus,
        completed_at: new Date().toISOString(),
      };
      // Prepend (most-recent first) and cap at MAX_RECENT
      return [entry, ...prev].slice(0, MAX_RECENT);
    });
  }, []);

  // ── Prune stale recently-completed entries periodically ────────
  useEffect(() => {
    const pruneInterval = setInterval(() => {
      const cutoff = Date.now() - RECENT_TTL_MS;
      setRecentlyCompleted(
        (prev) => prev.filter((r) => new Date(r.completed_at).getTime() > cutoff), // NOSONAR — arrow callback in state updater; complexity inherent to TTL filtering
      );
    }, 60_000); // check every minute
    return () => clearInterval(pruneInterval);
  }, []);

  // Fetch active operations from backend
  const fetchActive = useCallback(async () => {
    try {
      const res = await operationService.getActive();
      setOperations(res.data);
    } catch (err) {
      console.error("Failed to fetch active operations:", err);
    }
  }, []);

  // ── Initial fetch + periodic refresh ─────────────────────────

  useEffect(() => {
    if (!isAuthenticated) {
      setOperations([]);
      setRecentlyCompleted([]);
      return;
    }

    fetchActive();

    // Periodic refresh — keeps the header indicator in sync even if
    // a status-changed event was missed (e.g. tab backgrounded).
    const interval = setInterval(() => {
      if (isAuthenticatedRef.current) {
        fetchActive();
      }
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchActive]);

  // ── Listen for operation_status_changed events from NotificationContext ──

  useNotificationEvent("operation_status_changed", (detail) => {
    if (!detail?.operation_id) return;

    const { operation_id, status, operation_type, title } = detail;

    if (status === "running") {
      // New or resumed operation — add to tracked list
      setOperations((prev) => {
        // NOSONAR — state updater with find-then-upsert logic; complexity inherent to reconciliation
        const existing = prev.find((op) => op.operation_id === operation_id);
        if (existing) {
          return prev.map(
            (
              op, // NOSONAR — arrow callback in state updater map; part of reconciliation logic
            ) => (op.operation_id === operation_id ? { ...op, status, updated_at: new Date().toISOString() } : op),
          );
        }
        // Add new operation with what we know from the event
        return [
          ...prev,
          {
            operation_id,
            operation_type,
            entity_id: detail.entity_id || "",
            status,
            title,
            started_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user_id: 0, // Not needed for frontend display
          },
        ];
      });
    } else if (status === "completed" || status === "failed") {
      // Terminal — move to recently completed, then remove from active
      setOperations((prev) => {
        // NOSONAR — state updater with find-then-move logic; complexity inherent to terminal-state handling
        const op = prev.find((o) => o.operation_id === operation_id);
        if (op) {
          moveToRecent(op, status);
        } else {
          // Operation wasn't in active list (e.g. page was refreshed) —
          // synthesize a minimal entry for the recent list.
          moveToRecent(
            {
              operation_id,
              operation_type,
              entity_id: detail.entity_id || "",
              status,
              title,
              started_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              user_id: 0,
            },
            status,
          );
        }
        return prev.filter((o) => o.operation_id !== operation_id);
      });
    } else {
      // Paused or other intermediate status — update in place
      setOperations(
        (
          prev, // NOSONAR — state updater for intermediate status; complexity inherent to React functional update pattern
        ) =>
          prev.map(
            (
              op, // NOSONAR — arrow callback in state updater map; updates matching operation in place
            ) => (op.operation_id === operation_id ? { ...op, status, updated_at: new Date().toISOString() } : op),
          ),
      );
    }
  });

  // ── Mutation helpers ─────────────────────────────────────────────

  const getOperation = useCallback((opId: string) => operations.find((op) => op.operation_id === opId), [operations]);

  const findByEntityId = useCallback(
    (entityId: string) => operations.find((op) => op.entity_id === entityId && op.status === "running"),
    [operations],
  );

  const updateOperationStatus = useCallback(
    (opId: string, status: string) => {
      if (status === "completed" || status === "failed") {
        setOperations((prev) => {
          const op = prev.find((o) => o.operation_id === opId);
          if (op) moveToRecent(op, status);
          return prev.filter((o) => o.operation_id !== opId);
        });
      } else {
        setOperations((prev) =>
          prev.map((op) => (op.operation_id === opId ? { ...op, status, updated_at: new Date().toISOString() } : op)),
        );
      }
    },
    [moveToRecent],
  );

  const trackOperation = useCallback((op: ActiveOperation) => {
    setOperations((prev) => {
      // Avoid duplicates
      const existing = prev.find((o) => o.operation_id === op.operation_id);
      if (existing) {
        return prev.map((o) => (o.operation_id === op.operation_id ? op : o));
      }
      return [...prev, op];
    });
  }, []);

  const removeOperation = useCallback((opId: string) => {
    setOperations((prev) => prev.filter((op) => op.operation_id !== opId));
  }, []);

  const value = useMemo(
    () => ({
      activeOperations: operations,
      recentlyCompleted,
      operationCount: operations.length,
      getOperation,
      findByEntityId,
      refresh: fetchActive,
      updateOperationStatus,
      trackOperation,
      removeOperation,
    }),
    [
      operations,
      recentlyCompleted,
      getOperation,
      findByEntityId,
      fetchActive,
      updateOperationStatus,
      trackOperation,
      removeOperation,
    ],
  );

  return <OperationContext.Provider value={value}>{children}</OperationContext.Provider>;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useOperations() {
  const context = useContext(OperationContext);
  if (!context) {
    throw new Error("useOperations must be used within an OperationProvider");
  }
  return context;
}
